import { call, put, takeLatest, takeEvery } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import fileDownload from 'js-file-download';
import { notificationContainer } from 'utils/notificationContainer';
import { api } from 'services/api';
import {
	TOrderListRequest,
	TOrderListResponse,
	TOrderSplitsResponse,
	TOrderItemsResponse,
	TSplitItemsBySplitIdResponse,
	TSplitItemsSuccessPayload,
	TOrderReturnsResponse,
	TOrderReturnItemsResponse,
	TOrderDiscountsResponse,
	TOrderDiscountBreakdownResponse,
	TSwissDeclarationsRequest,
	TSwissDeclarationsResponse,
	TDownloadSwissDeclarationRequest,
} from './types';
import {
	getOrderListRequest,
	getOrderListSuccess,
	getOrderListFailure,
	getOrderSplitsRequest,
	getOrderSplitsSuccess,
	getOrderSplitsFailure,
	getOrderItemsRequest,
	getOrderItemsSuccess,
	getOrderItemsFailure,
	getSplitItemsBySplitIdRequest,
	getSplitItemsBySplitIdSuccess,
	getSplitItemsBySplitIdFailure,
	getOrderReturnsRequest,
	getOrderReturnsSuccess,
	getOrderReturnsFailure,
	getOrderReturnItemsRequest,
	getOrderReturnItemsSuccess,
	getOrderReturnItemsFailure,
	getOrderDiscountsRequest,
	getOrderDiscountsSuccess,
	getOrderDiscountsFailure,
	getOrderDiscountBreakdownRequest,
	getOrderDiscountBreakdownSuccess,
	getOrderDiscountBreakdownFailure,
	getSwissDeclarationsRequest,
	getSwissDeclarationsSuccess,
	getSwissDeclarationsFailure,
	downloadSwissDeclarationRequest,
	downloadSwissDeclarationSuccess,
	downloadSwissDeclarationFailure,
} from './reducer';

function* getOrderListWorker(action: PayloadAction<TOrderListRequest>) {
	try {
		const response: TOrderListResponse = yield call(
			api.orderManagement.getOrderList,
			action.payload,
		);
		yield put(getOrderListSuccess(response));
	} catch (error) {
		yield put(getOrderListFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* getOrderSplitsWorker(action: PayloadAction<number>) {
	try {
		const response: TOrderSplitsResponse = yield call(
			api.orderManagement.getOrderSplits,
			action.payload,
		);
		yield put(getOrderSplitsSuccess(response));
	} catch (error) {
		yield put(getOrderSplitsFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* getOrderItemsWorker(action: PayloadAction<number>) {
	try {
		const response: TOrderItemsResponse = yield call(
			api.orderManagement.getOrderItems,
			action.payload,
		);
		yield put(getOrderItemsSuccess(response));
	} catch (error) {
		yield put(getOrderItemsFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* getOrderReturnsWorker(action: PayloadAction<TOrderListRequest>) {
	try {
		const response: TOrderReturnsResponse = yield call(
			api.orderManagement.getOrderReturns,
			action.payload,
		);
		yield put(getOrderReturnsSuccess(response));
	} catch (error) {
		yield put(getOrderReturnsFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* getOrderReturnItemsWorker(action: PayloadAction<TOrderListRequest>) {
	try {
		const response: TOrderReturnItemsResponse = yield call(
			api.orderManagement.getOrderReturnItems,
			action.payload,
		);
		yield put(getOrderReturnItemsSuccess(response));
	} catch (error) {
		yield put(getOrderReturnItemsFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* getOrderDiscountsWorker(action: PayloadAction<TOrderListRequest>) {
	try {
		const response: TOrderDiscountsResponse = yield call(
			api.orderManagement.getOrderDiscounts,
			action.payload,
		);
		yield put(getOrderDiscountsSuccess(response));
	} catch (error) {
		yield put(getOrderDiscountsFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* getOrderDiscountBreakdownWorker(action: PayloadAction<TOrderListRequest>) {
	try {
		const response: TOrderDiscountBreakdownResponse = yield call(
			api.orderManagement.getOrderDiscountBreakdown,
			action.payload,
		);
		yield put(getOrderDiscountBreakdownSuccess(response));
	} catch (error) {
		yield put(getOrderDiscountBreakdownFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* getSplitItemsBySplitIdWorker(action: PayloadAction<string>) {
	const splitId = action.payload;
	try {
		const response: TSplitItemsBySplitIdResponse = yield call(
			api.orderManagement.getSplitItemsBySplitId,
			splitId,
		);
		const payload: TSplitItemsSuccessPayload = { splitId, data: response?.rows ?? [] };
		yield put(getSplitItemsBySplitIdSuccess(payload));
	} catch (error) {
		yield put(getSplitItemsBySplitIdFailure(splitId));
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* getSwissDeclarationsWorker(action: PayloadAction<TSwissDeclarationsRequest>) {
	try {
		const response: TSwissDeclarationsResponse = yield call(
			api.orderManagement.getSwissDeclarations,
			action.payload,
		);
		yield put(getSwissDeclarationsSuccess(response));
	} catch (error) {
		yield put(getSwissDeclarationsFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* downloadSwissDeclarationWorker(action: PayloadAction<TDownloadSwissDeclarationRequest>) {
	try {
		const blob: Blob = yield call(api.orderManagement.downloadSwissDeclaration, action.payload);
		yield put(downloadSwissDeclarationSuccess());
		if (blob) {
			fileDownload(blob, action.payload.fileName || `swiss_declaration_${action.payload.id}`);
		}
	} catch (error) {
		yield put(downloadSwissDeclarationFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

export function* orderManagementSaga() {
	yield takeLatest(getOrderListRequest.type, getOrderListWorker);
	yield takeLatest(getOrderSplitsRequest.type, getOrderSplitsWorker);
	yield takeLatest(getOrderItemsRequest.type, getOrderItemsWorker);
	yield takeEvery(getSplitItemsBySplitIdRequest.type, getSplitItemsBySplitIdWorker);
	yield takeLatest(getOrderReturnsRequest.type, getOrderReturnsWorker);
	yield takeLatest(getOrderReturnItemsRequest.type, getOrderReturnItemsWorker);
	yield takeLatest(getOrderDiscountsRequest.type, getOrderDiscountsWorker);
	yield takeLatest(getOrderDiscountBreakdownRequest.type, getOrderDiscountBreakdownWorker);
	yield takeLatest(getSwissDeclarationsRequest.type, getSwissDeclarationsWorker);
	yield takeEvery(downloadSwissDeclarationRequest.type, downloadSwissDeclarationWorker);
}
